import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

/**
 * Weekly realized-P/L bar chart for the Dashboard.
 *
 * Each bar is one ISO week's net realized P/L. Green when net positive, red
 * when net negative, muted when the user booked nothing that week. Hidden
 * entirely for accounts with zero realized P/L across the full window so a
 * fresh user's dashboard doesn't show an empty chart.
 */
export default function RealizedPLSparkline({ weeks = 12 }) {
  const [series, setSeries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get(`/me/realized/weekly?weeks=${weeks}`)
      .then((r) => setSeries(r.data.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoaded(true));
  }, [weeks]);

  if (!loaded) return null;
  const hasAny = series.some((s) => s.realized_pl !== 0 || s.wins > 0 || s.losses > 0);
  if (!hasAny) return null;

  const totalWins = series.reduce((s, r) => s + r.wins, 0);
  const totalLosses = series.reduce((s, r) => s + r.losses, 0);
  const netPL = series.reduce((s, r) => s + r.realized_pl, 0);
  const positive = netPL >= 0;

  const fmt = (n) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? "-" : n > 0 ? "+" : "";
    return abs >= 1000
      ? `${sign}$${(abs / 1000).toFixed(1)}k`
      : `${sign}$${abs.toFixed(0)}`;
  };

  return (
    <div
      className="p-4 border border-zinc-800 rounded-sm bg-zinc-900/40"
      data-testid="realized-pl-sparkline"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Realized P/L · {weeks}w
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <div
              className={`mono text-2xl font-bold ${positive ? "profit" : "loss"}`}
              data-testid="realized-pl-total"
            >
              {fmt(netPL)}
            </div>
          </div>
          <div className="mono text-[10px] mt-1 text-zinc-500">
            <span className="text-[#00C805]">{totalWins}W</span> ·{" "}
            <span className="text-[#FF3B30]">{totalLosses}L</span>
          </div>
        </div>
      </div>
      <div className="h-16 mt-2 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="week_start" hide />
            <YAxis hide domain={["auto", "auto"]} />
            <ReferenceLine y={0} stroke="#3f3f46" strokeDasharray="2 2" />
            <Tooltip
              contentStyle={{
                background: "#18181b",
                border: "1px solid #27272a",
                borderRadius: 2,
                fontFamily: "JetBrains Mono",
                fontSize: 11,
              }}
              cursor={{ fill: "rgba(255,255,255,0.03)" }}
              formatter={(v, _n, p) => {
                const w = p?.payload?.wins ?? 0;
                const l = p?.payload?.losses ?? 0;
                return [`${fmt(v)} (${w}W · ${l}L)`, "Realized"];
              }}
              labelFormatter={(v) => `Week of ${v}`}
              labelStyle={{ color: "#a1a1aa" }}
            />
            <Bar dataKey="realized_pl" isAnimationActive={false} radius={[2, 2, 0, 0]}>
              {series.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={
                    entry.realized_pl > 0
                      ? "#00C805"
                      : entry.realized_pl < 0
                      ? "#FF3B30"
                      : "#3f3f46"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
